/**
 * Modal create/edit form shared by the board and thread sections. Pure
 * presentation — the caller owns the mutation and passes it as `onSubmit`.
 */

import React, { useState } from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import DatePicker from '@amzn/awsui-components-core/date-picker';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Modal from '@amzn/awsui-components-core/modal';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Textarea from '@amzn/awsui-components-core/textarea';
import {
  ActionItemDraft,
  ActionItemRecord,
  ActionItemStatus,
} from 'src/types/actionItems.types';
import {
  ACTION_ITEM_HEADING_MAX,
  ACTION_ITEM_MESSAGE_MAX,
} from 'src/constants/actionItems';

import { STATUS_LABELS } from './ActionItemStatus';

const STATUS_OPTIONS = (
  Object.keys(STATUS_LABELS) as ActionItemStatus[]
).map((value) => ({ value, label: STATUS_LABELS[value] }));

export interface ActionItemFormProps {
  /** Modal title, e.g. "Create action item" / "Edit action item". */
  title: string;
  /** Existing record when editing; null when creating. */
  existing: ActionItemRecord | null;
  /** Submits the authored draft; the caller owns which mutation runs. */
  onSubmit: (draft: ActionItemDraft) => Promise<unknown>;
  onDismiss: () => void;
}

export const ActionItemForm: React.FC<ActionItemFormProps> = ({
  title,
  existing,
  onSubmit,
  onDismiss,
}) => {
  const [heading, setHeading] = useState(existing?.heading ?? '');
  const [message, setMessage] = useState(existing?.message ?? '');
  const [status, setStatus] = useState<ActionItemStatus>(
    existing?.status ?? 'OPEN',
  );
  const [assignee, setAssignee] = useState(existing?.assignee ?? '');
  const [eta, setEta] = useState(existing?.eta ?? '');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Enforce the backend length bounds in the form instead of surfacing a 400.
  const headingTooLong = heading.trim().length > ACTION_ITEM_HEADING_MAX;
  const messageTooLong = message.trim().length > ACTION_ITEM_MESSAGE_MAX;

  const submit = async () => {
    setSaving(true);
    setSubmitError(null);
    try {
      await onSubmit({
        heading: heading.trim(),
        message: message.trim() || undefined,
        status,
        assignee: assignee.trim(),
        eta,
      });
      onDismiss();
    } catch {
      // Keep the modal open so nothing authored is lost.
      setSubmitError('Could not save the action item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={true}
      size="medium"
      onDismiss={onDismiss}
      closeAriaLabel="Close"
      header={title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={
                saving ||
                !heading.trim() ||
                !assignee.trim() ||
                !eta ||
                headingTooLong ||
                messageTooLong
              }
              loading={saving}
              data-testid="action-item-save"
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {submitError && (
          <Box color="text-status-error" fontSize="body-s">
            {submitError}
          </Box>
        )}
        <FormField
          label="Heading"
          constraintText={`Up to ${ACTION_ITEM_HEADING_MAX} characters`}
          errorText={
            headingTooLong
              ? `Headings are limited to ${ACTION_ITEM_HEADING_MAX} characters.`
              : undefined
          }
        >
          <Input
            value={heading}
            onChange={({ detail }) => setHeading(detail.value)}
            placeholder="Short title for the follow-up"
            data-testid="action-item-heading"
          />
        </FormField>
        <FormField
          label={<span>Message <i>- optional</i></span>}
          constraintText={`Up to ${ACTION_ITEM_MESSAGE_MAX.toLocaleString()} characters`}
          errorText={
            messageTooLong
              ? `Messages are limited to ${ACTION_ITEM_MESSAGE_MAX.toLocaleString()} characters.`
              : undefined
          }
        >
          <Textarea
            value={message}
            onChange={({ detail }) => setMessage(detail.value)}
            rows={4}
            data-testid="action-item-message"
          />
        </FormField>
        <FormField label="Status">
          <Select
            selectedOption={{ value: status, label: STATUS_LABELS[status] }}
            options={STATUS_OPTIONS}
            onChange={({ detail }) =>
              setStatus(detail.selectedOption.value as ActionItemStatus)
            }
            data-testid="action-item-status"
          />
        </FormField>
        <FormField label="Assignee" constraintText="Required">
          <Input
            value={assignee}
            onChange={({ detail }) => setAssignee(detail.value)}
            placeholder="Amazon alias"
            data-testid="action-item-assignee"
          />
        </FormField>
        <FormField label="ETA" constraintText="Required">
          <DatePicker
            value={eta}
            onChange={({ detail }) => setEta(detail.value)}
            placeholder="YYYY-MM-DD"
            i18nStrings={{
              openCalendarAriaLabel: (selected) =>
                selected ? `Choose ETA, selected date is ${selected}` : 'Choose ETA',
            }}
            data-testid="action-item-eta"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
